import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import type {
  CommissionPolicy,
  CommissionPolicyCreatePayload,
  CommissionPolicyUpdatePayload,
} from '@/types/commission-policy.types';
import { CommercialMode } from '@/types/commission-policy.types';
import {
  useCreateCommissionPolicyMutation,
  useCreateGlobalCommissionPolicyMutation,
  useDeactivateCommissionPolicyMutation,
  useUpdateCommissionPolicyMutation,
} from '@/services/CommissionPolicyService';
import { usePermissions } from '@/hooks/usePermissions';
import NumberInput from '@/components/inputControls/NumberInput';

type Mode = 'create' | 'edit' | 'view';

type FormState = {
  commercial_mode: CommercialMode;
  commission_rate: number | '';
  effective_from: string;
  active: boolean;
};

const normalizeDateOnly = (value?: string | null) => {
  if (!value) return '';
  return value.includes('T') ? value.split('T')[0] : value;
};

export function CommissionPolicyForm({
  mode,
  policy,
  actorPayload,
  onDone,
  readOnlyReason,
}: {
  mode: Mode;
  policy: CommissionPolicy | null;
  actorPayload?: Pick<CommissionPolicyCreatePayload, 'actor_type' | 'actor_id'>;
  onDone: () => void;
  readOnlyReason?: string;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { hasPermission } = usePermissions();
  const canRead = hasPermission('commission_policy_management_read');
  const canCreate = hasPermission('commission_policy_management_create');
  const canUpdate = hasPermission('commission_policy_management_update');
  const canDelete = hasPermission('commission_policy_management_delete');

  const [createPolicy, { isLoading: isCreating }] =
    useCreateCommissionPolicyMutation();
  const [createGlobalPolicy, { isLoading: isCreatingGlobal }] =
    useCreateGlobalCommissionPolicyMutation();
  const [updatePolicy, { isLoading: isUpdating }] =
    useUpdateCommissionPolicyMutation();
  const [deactivatePolicy, { isLoading: isDeactivating }] =
    useDeactivateCommissionPolicyMutation();

  const lacksReadPermission = !canRead;
  const lacksCreatePermission = mode === 'create' && !canCreate;
  const lacksUpdatePermission = mode === 'edit' && !canUpdate;
  const isReadOnly =
    mode === 'view' ||
    !!readOnlyReason ||
    lacksReadPermission ||
    lacksCreatePermission ||
    lacksUpdatePermission;

  const initialState = useMemo<FormState>(() => {
    if (policy) {
      return {
        commercial_mode: policy.commercial_mode,
        commission_rate: policy.commission_rate,
        effective_from: normalizeDateOnly(policy.effective_from),
        active: policy.active,
      };
    }

    return {
      commercial_mode: CommercialMode.COMMISSION_ONLY,
      commission_rate: '',
      effective_from: '',
      active: true,
    };
  }, [policy]);

  const [formData, setFormData] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData(initialState);
    setErrors({});
  }, [initialState]);

  useEffect(() => {
    if (formData.commercial_mode === CommercialMode.SUBSCRIPTION_ONLY) {
      setFormData((prev) => ({ ...prev, commission_rate: 0 }));
      setErrors((prev) => ({ ...prev, commission_rate: '' }));
    }
  }, [formData.commercial_mode]);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        commercial_mode: Yup.mixed<CommercialMode>()
          .oneOf(Object.values(CommercialMode) as CommercialMode[])
          .required('Commercial mode is required'),
        commission_rate:
          formData.commercial_mode === CommercialMode.SUBSCRIPTION_ONLY
            ? Yup.number()
                .transform((v, o) => (o === '' ? 0 : v))
                .typeError('Commission rate must be a number')
                .min(0)
                .max(0)
                .required()
            : Yup.number()
                .transform((v, o) => (o === '' ? undefined : v))
                .typeError('Commission rate must be a number')
                .min(0, 'Min 0')
                .max(100, 'Max 100')
                .test('max-2-decimals', 'Max 2 decimal places', (val) =>
                  val == null
                    ? false
                    : /^\d{1,3}(\.\d{1,2})?$/.test(String(val))
                )
                .required('Commission rate is required'),
        effective_from: Yup.date()
          .transform((v, o) => (o === '' ? undefined : v))
          .typeError('Select valid effective from date')
          .required('Effective from is required'),
        active: Yup.boolean().required(),
      }),
    [formData.commercial_mode]
  );

  const isBusy = isCreating || isCreatingGlobal || isUpdating || isDeactivating;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const name = target.name as keyof FormState;

    let nextValue: string | number | boolean = (target as HTMLSelectElement)
      .value;

    if ('type' in target && (target as HTMLInputElement).type === 'checkbox') {
      nextValue = (target as HTMLInputElement).checked;
    } else if (
      'type' in target &&
      (target as HTMLInputElement).type === 'number'
    ) {
      const raw = (target as HTMLInputElement).value;
      if (raw === '') {
        nextValue = '';
      } else {
        const num = Number(raw);
        nextValue = Number.isNaN(num) ? '' : num;
      }
    }

    if (name === 'commission_rate' && nextValue !== '') {
      const num = Number(nextValue);
      const clamped = Math.min(Math.max(num, 0), 100);
      const rounded = Math.round(clamped * 100) / 100;
      nextValue = rounded;
    }

    setFormData((prev) => ({ ...prev, [name]: nextValue as never }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});

      const commissionRate =
        formData.commercial_mode === CommercialMode.SUBSCRIPTION_ONLY
          ? 0
          : typeof formData.commission_rate === 'string'
            ? Number(formData.commission_rate)
            : formData.commission_rate;

      if (mode === 'create') {
        if (actorPayload?.actor_id) {
          const payload: CommissionPolicyCreatePayload = {
            ...actorPayload,
            commercial_mode: formData.commercial_mode,
            commission_rate: commissionRate,
            effective_from: formData.effective_from,
            active: formData.active,
          };

          if (!canCreate) {
            enqueueSnackbar(
              "You don't have permission to create commission policies.",
              {
                variant: 'error',
              }
            );
            return;
          }

          await createPolicy(payload).unwrap();
        } else {
          if (!canCreate) {
            enqueueSnackbar(
              "You don't have permission to create commission policies.",
              {
                variant: 'error',
              }
            );
            return;
          }

          await createGlobalPolicy({
            commercial_mode: formData.commercial_mode,
            commission_rate: commissionRate,
            effective_from: formData.effective_from,
            active: formData.active,
          }).unwrap();
        }
        enqueueSnackbar('Commission policy created successfully', {
          variant: 'success',
        });
        onDone();
        return;
      }

      if (!policy) {
        enqueueSnackbar('No commission policy found to update', {
          variant: 'error',
        });
        return;
      }

      if (!formData.active && policy.active) {
        if (!canDelete) {
          enqueueSnackbar(
            "You don't have permission to deactivate commission policies.",
            {
              variant: 'error',
            }
          );
          return;
        }
        await deactivatePolicy(policy.id).unwrap();
        enqueueSnackbar('Commission policy deactivated successfully', {
          variant: 'success',
        });
        onDone();
        return;
      }

      const payload: CommissionPolicyUpdatePayload = {
        commercial_mode: formData.commercial_mode,
        commission_rate: commissionRate,
        effective_from: formData.effective_from,
        active: formData.active,
      };

      const updatePayload = actorPayload?.actor_id
        ? {
            ...payload,
            actor_id: actorPayload.actor_id,
            actor_type: actorPayload.actor_type,
          }
        : payload;

      if (!canUpdate) {
        enqueueSnackbar(
          "You don't have permission to update commission policies.",
          {
            variant: 'error',
          }
        );
        return;
      }

      await updatePolicy({ id: policy.id, payload: updatePayload }).unwrap();
      enqueueSnackbar('Commission policy updated successfully', {
        variant: 'success',
      });
      onDone();
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((ve) => {
          if (ve.path) newErrors[ve.path] = ve.message;
        });
        setErrors(newErrors);
        return;
      }

      const apiMessage = (err as { data?: { message?: string } })?.data
        ?.message;
      enqueueSnackbar(apiMessage || 'Failed to save commission policy', {
        variant: 'error',
      });
    }
  };

  const commissionDisabled =
    isReadOnly || formData.commercial_mode === CommercialMode.SUBSCRIPTION_ONLY;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {readOnlyReason && (
          <div className="md:col-span-2 text-sm text-gray-600">
            {readOnlyReason}
          </div>
        )}

        <div>
          <label htmlFor="commercial_mode" className="form-label">
            Commercial Mode
          </label>
          <select
            id="commercial_mode"
            name="commercial_mode"
            value={formData.commercial_mode}
            onChange={handleChange}
            className="select w-full"
            disabled={isReadOnly}
          >
            <option value={CommercialMode.SUBSCRIPTION_ONLY}>
              Subscription only
            </option>
            <option value={CommercialMode.COMMISSION_ONLY}>
              Commission only
            </option>
            <option value={CommercialMode.HYBRID}>Hybrid</option>
          </select>
          {errors.commercial_mode && (
            <div className="text-danger text-xs mt-1">
              {errors.commercial_mode}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="commission_rate" className="form-label">
            Commission Rate (%)
          </label>
          <NumberInput
            id="commission_rate"
            name="commission_rate"
            value={formData.commission_rate}
            onChange={handleChange}
            className="input w-full"
            min={0}
            max={100}
            step={0.01}
            disabled={commissionDisabled}
            autoComplete="off"
          />
          {errors.commission_rate && (
            <div className="text-danger text-xs mt-1">
              {errors.commission_rate}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="effective_from" className="form-label">
            Effective From
          </label>
          <input
            id="effective_from"
            type="date"
            name="effective_from"
            value={formData.effective_from}
            onChange={handleChange}
            className="input w-full"
            disabled={isReadOnly}
            autoComplete="off"
          />
          {errors.effective_from && (
            <div className="text-danger text-xs mt-1">
              {errors.effective_from}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input
            id="commission-policy-active"
            type="checkbox"
            name="active"
            checked={formData.active}
            onChange={handleChange}
            className="cursor-pointer"
            disabled={isReadOnly}
          />
          <label
            htmlFor="commission-policy-active"
            className="text-sm cursor-pointer"
          >
            Active
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" className="btn btn-light" onClick={onDone}>
          {isReadOnly ? 'Back' : 'Cancel'}
        </button>
        {!isReadOnly && (
          <button type="submit" className="btn btn-primary" disabled={isBusy}>
            {isBusy ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
    </form>
  );
}
